import { FormApi } from "final-form";
import React from "react";
import { Form, useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import z from "zod";

import { useGetEmailConfig } from "@admin/hooks/useGetEmailConfig";
import { usePostEmailConfig } from "@admin/hooks/usePostEmailConfig";
import { usePutEmailConfig } from "@admin/hooks/usePutEmailConfig";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { PrimaryButton, SecondaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { LoadingBox } from "@shared/Loading";

import { emailConfigValidationSchema } from "./AdminEmailPage.validation";

const useStyles = makeStyles()((theme: Theme) => ({
    section: { paddingBottom: 0 },
    alert: { marginBottom: theme.spacing(2) },
    formBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2), padding: theme.spacing(2, 0) },
    buttonBox: { display: "flex", justifyContent: "flex-end", gap: theme.spacing(2) },
}));

const mailServices = [
    { label: "SMTP", value: "smtp" },
    { label: "Mailjet", value: "mailjet" },
];

const FormContent = ({
    onSendTestEmail,
    isUpdating,
    isSendingTestEmail,
}: {
    onSendTestEmail: () => void;
    isUpdating: boolean;
    isSendingTestEmail: boolean;
}) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const { values } = useFormState<API.EmailConfig>();

    const mailService = values?.mailService;

    return (
        <>
            <SectionContainer className={classes.section}>
                <SectionHeader>{t("pages:adminEmailPage.service.title")}</SectionHeader>
                <Box className={classes.formBox}>
                    <ControlledSelectField
                        name="mailService"
                        label={t("pages:adminEmailPage.service.activeService")}
                        options={mailServices}
                    />
                </Box>
            </SectionContainer>
            <SectionContainer className={classes.section}>
                <SectionHeader>{t("pages:adminEmailPage.address.title")}</SectionHeader>
                <Box className={classes.formBox}>
                    <TextField name="senderMail" label={t("pages:adminEmailPage.address.email")} fullWidth />
                    <TextField name="senderName" label={t("pages:adminEmailPage.address.name")} fullWidth />
                </Box>
            </SectionContainer>
            {mailService === "smtp" && (
                <SectionContainer className={classes.section}>
                    <SectionHeader>{t("pages:adminEmailPage.service.smtp.title")}</SectionHeader>
                    <Box className={classes.formBox}>
                        <TextField name="smtpHost" label={t("pages:adminEmailPage.service.smtp.host")} fullWidth />
                        <TextField name="smtpPort" label={t("pages:adminEmailPage.service.smtp.port")} fullWidth />
                        <TextField name="smtpUser" label={t("pages:adminEmailPage.service.smtp.user")} fullWidth />
                        <TextField name="smtpPassword" label={t("common:label.password")} type="password" fullWidth />
                    </Box>
                </SectionContainer>
            )}
            {mailService === "mailjet" && (
                <SectionContainer className={classes.section}>
                    <SectionHeader>{t("pages:adminEmailPage.service.mailjet.title")}</SectionHeader>
                    <Box className={classes.formBox}>
                        <TextField name="mailjetApiUser" label={t("pages:adminEmailPage.service.mailjet.user")} fullWidth />
                        <TextField
                            name="mailjetApiPass"
                            label={t("pages:adminEmailPage.service.mailjet.password")}
                            type="password"
                            fullWidth
                        />
                        <TextField
                            name="mailjetHookCredentials"
                            label={t("pages:adminEmailPage.service.mailjet.hookCredentials")}
                            fullWidth
                        />
                    </Box>
                </SectionContainer>
            )}
            <Box className={classes.buttonBox}>
                <SecondaryButton
                    disabled={isUpdating || isSendingTestEmail}
                    loading={isSendingTestEmail}
                    onClick={onSendTestEmail}
                >
                    {t("pages:adminEmailPage.sendTestEmail")}
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isUpdating || isSendingTestEmail} loading={isUpdating}>
                    {t("common:button.save")}
                </PrimaryButton>
            </Box>
        </>
    );
};

const AdminEmailPage = () => {
    const { isLoading: isInitializing, data: emailConfig, refetch: refetchEmailConfig } = useGetEmailConfig();
    const { isLoading: isUpdating, onMutate: onUpdateEmailConfig } = usePutEmailConfig(refetchEmailConfig);
    const { isLoading: isSendingTestEmail, onMutate: onSendTestEmail } = usePostEmailConfig();

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const validate = useValidationSchema(emailConfigValidationSchema as z.ZodType<API.EmailConfig>);

    const onSubmit = (values: API.EmailConfig, form: FormApi<API.EmailConfig>) => {
        if (!emailConfig) {
            return;
        }

        const cleanedValues: API.EmailConfig = {
            ...emailConfig,
            ...values,
            smtpPort: typeof values.smtpPort === "string" ? Number(values.smtpPort) || 0 : values.smtpPort || 0,
        };

        onUpdateEmailConfig(cleanedValues);
        form.restart(cleanedValues);
    };

    if (isInitializing) {
        return <LoadingBox />;
    }

    if (!emailConfig) {
        return null;
    }

    return (
        <PageContainer fullWidth={false}>
            <CustomAlert severity="warning" className={classes.alert}>
                {t("pages:adminEmailPage.serverRestartWarning")}
            </CustomAlert>

            <Form<API.EmailConfig>
                destroyOnUnregister={false}
                onSubmit={onSubmit}
                initialValues={emailConfig}
                validate={validate}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <FormContent
                            onSendTestEmail={onSendTestEmail}
                            isUpdating={isUpdating}
                            isSendingTestEmail={isSendingTestEmail}
                        />
                    </form>
                )}
            />
        </PageContainer>
    );
};

export default withMetaDescription(AdminEmailPage, {
    title: i18next.t("pages:adminEmailPage.metaTitle"),
    description: i18next.t("pages:adminEmailPage.metaDescription"),
});
